/**
 * 音色库的服务端接口。
 *
 * 跟 lib/tts-voice.ts 的分工：那边是 localStorage（现在只当本地缓存，让页面
 * 首屏不闪），这边是唯一真相。选中的音色存在 users.voice_slug，所以换电脑、
 * 换手机、网页转 App 都跟着账号走。
 *
 * 注意 GET /v1/tts/voices 和这里的 GET /api/voices 是两个不同的东西：
 * 前者是给 API 使用者看的目录（只有 id/name/desc），后者才带 added/selected/
 * builtin，是设置页的数据源。别接错。
 */

import { apiFetch } from "@/lib/api";

export type ServerVoice = {
  slug: string;
  name: string;
  desc: string;
  /** true = 内置音色：所有人共享的兜底，不能改名不能删 */
  builtin: boolean;
  /** 已加进「我的音色」 */
  added: boolean;
  /** 当前正在用的那个 */
  selected: boolean;
};

export const voicesApi = {
  list: () => apiFetch<ServerVoice[]>("/api/voices"),

  /** 点「使用」。落到 users.voice_slug，所以是跨设备的。 */
  select: (slug: string) =>
    apiFetch<void>(`/api/voices/${encodeURIComponent(slug)}/select`, { method: "POST" }),

  add: (slug: string) =>
    apiFetch<void>(`/api/voices/${encodeURIComponent(slug)}/add`, { method: "POST" }),

  remove: (slug: string) =>
    apiFetch<void>(`/api/voices/${encodeURIComponent(slug)}/add`, { method: "DELETE" }),

  rename: (slug: string, name: string) =>
    apiFetch<ServerVoice>(`/api/voices/${encodeURIComponent(slug)}`, {
      method: "PATCH",
      body: JSON.stringify({ name })
    }),

  destroy: (slug: string) =>
    apiFetch<void>(`/api/voices/${encodeURIComponent(slug)}`, { method: "DELETE" }),

  /**
   * 用一段录音创建自定义音色。
   *
   * promptText 必须与录音**逐字一致**——这是 CosyVoice 的硬要求，错一个字
   * 克隆质量就崩。所以调用方必须先 ASR 转写、再让用户确认修正，不能直接把
   * 识别结果送进来。
   */
  create: (name: string, promptText: string, audioBase64: string) =>
    apiFetch<ServerVoice>("/api/voices", {
      method: "POST",
      body: JSON.stringify({ name, prompt_text: promptText, audio: audioBase64 })
    })
};

/**
 * 下载自己那段参考录音。
 *
 * 不走 apiFetch：那个拿到响应就 .json()，音频字节会炸在解析上。
 * 别人的音色在服务端返回 404（不是 403）——403 等于承认"存在但你没权限"，
 * 光靠枚举 slug 就能测出平台上有哪些音色。
 */
export async function downloadVoiceAudio(slug: string, filename: string): Promise<void> {
  const { ensureFreshAccessToken } = await import("@/lib/api");
  const token = await ensureFreshAccessToken();
  if (!token) throw new Error("请先登录");

  const res = await fetch(`/api/voices/${encodeURIComponent(slug)}/audio`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: "no-store"
  });
  if (!res.ok) {
    throw new Error(res.status === 404 ? "录音不存在" : "下载失败，请稍后再试");
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".wav") ? filename : `${filename}.wav`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // 立刻 revoke 会让下载拿不到数据，给浏览器留一拍
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
